<template>
  <v-card>
    <v-card-text v-if="selectedBusinessUnit" class="pb-2">
      <vs-text-input v-model="search" label="Filter" data-test="new-form-selection-search">
        <template #append>
          <v-icon>search</v-icon>
        </template>
      </vs-text-input>
    </v-card-text>

    <v-list v-if="selectedBusinessUnit" class="form-list" data-test="new-form-selection-list">
      <div v-for="(categoryForms, category) in searchedForms" :key="category">
        <v-subheader class="list-subheader">
          {{ category }}
        </v-subheader>
        <v-list-item
          v-for="form in categoryForms"
          :key="form.id"
          :data-test="`new-form-selection-form-${form.id}`"
          @click="selectForm(form.id)"
        >
          {{ form.name }}
        </v-list-item>
      </div>

      <v-list-item v-if="Object.keys(searchedForms).length === 0 && filteredForms.length !== 0">
        <vs-text
          >No results for <i>{{ search }}</i>
        </vs-text>
      </v-list-item>
      <v-list-item v-else-if="filteredForms.length === 0">
        <vs-text>No forms found.</vs-text>
      </v-list-item>
    </v-list>

    <v-list v-else-if="availableBusinessUnits.length > 0" data-test="new-form-selection-bu-list">
      <v-subheader class="list-subheader">Who's it for?</v-subheader>
      <v-list-item
        v-for="businessUnit in availableBusinessUnits"
        :key="businessUnit.id"
        :disabled="businessUnit.disabled"
        :data-test="`new-form-selection-bu-${businessUnit.id}`"
        @click="selectBusinessUnit(businessUnit.id)"
      >
        {{ businessUnit.name }}
      </v-list-item>
    </v-list>

    <v-list v-else data-test="new-form-selection-no-forms">
      <v-list-item>
        <vs-text>No forms found.</vs-text>
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script setup lang="ts">
import { checkSearchResult } from '@/utils/search'
import { orderBy } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useGetters } from 'vuex-composition-helpers'
import Form from '@/models/form'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsText from '@/components/vision/VsText.vue'

const emit = defineEmits<{
  (e: 'select', value: { formId: string; businessUnitId: string }): void
}>()

interface Props {
  forms: Form[]
}

const props = defineProps<Props>()

const search = ref('')
const selectedBusinessUnit = ref('')

const { getAccessibleHubIds, getRolesForBusinessUnit, getOrganizationId } = useGetters(
  'permissions',
  ['getAccessibleHubIds', 'getRolesForBusinessUnit', 'getOrganizationId'],
)

const { getBusinessUnitById } = useGetters('businessUnits', ['getBusinessUnitById'])

const availableBusinessUnits = computed(() => {
  const accessibleBusinessUnits = [getOrganizationId.value, ...getAccessibleHubIds.value]
  return accessibleBusinessUnits
    .map((businessUnitId) => {
      const businessUnit = getBusinessUnitById.value(businessUnitId)
      if (!businessUnit) return null
      return businessUnit
    })
    .filter((businessUnit) => {
      return businessUnit != null && filterFormsByBusinessUnitId(businessUnit.id).length > 0
    })
})

const filteredForms = computed(() => {
  return filterFormsByBusinessUnitId(selectedBusinessUnit.value)
})

const searchedForms = computed((): Record<string, Form[]> => {
  const forms = filteredForms.value.filter((a) => checkSearchResult(search.value, a.name))
  let groupedForms = {}
  for (const form of forms) {
    const formCategoryName = form.category?.name ?? 'Uncategorized'
    if (groupedForms[formCategoryName] === undefined) {
      groupedForms[formCategoryName] = []
    }
    groupedForms[formCategoryName].push(form)
  }
  for (const [key] of Object.entries(groupedForms)) {
    groupedForms[key] = orderBy(groupedForms[key], [(form) => form.name.toLowerCase()])
  }
  groupedForms = Object.keys(groupedForms)
    .sort((a, b) => (a.toLowerCase() < b.toLowerCase() ? -1 : 1))
    .filter((key) => groupedForms[key].length > 0)
    .reduce((grouped, key) => Object.assign(grouped, { [key]: groupedForms[key] }), {})
  return groupedForms
})

onMounted(() => {
  if (selectedBusinessUnit.value === '' && availableBusinessUnits.value.length === 1) {
    selectedBusinessUnit.value = availableBusinessUnits.value[0].id
  }
})

function selectForm(formId: string) {
  emit('select', { formId, businessUnitId: selectedBusinessUnit.value })
}

function selectBusinessUnit(businessUnitId: string) {
  selectedBusinessUnit.value = businessUnitId
}

function filterFormsByBusinessUnitId(businessUnitId: string) {
  return props.forms.filter((f) => {
    const accessGrant = f.accessGrants.find((ag) => ag.businessUnitId === businessUnitId)
    if (!accessGrant) return false

    const userRoles = getRolesForBusinessUnit.value(businessUnitId)
    return userRoles.some((role) => !accessGrant.roleRestrictions.includes(role))
  })
}
</script>

<style scoped>
.form-list {
  overflow-y: auto;
  max-height: 320px;
}

.list-subheader {
  font-weight: 600;
  color: var(--color-black) !important;
  font-family: var(--font-display);
}
</style>
